import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { useSiteContent } from "@/contexts/SiteContentContext";


import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatPrice } from "@/lib/constants";
import { typeLabel } from "@/lib/i18n";
import { toast } from "sonner";
import { Star, StarOff, Trash2, CheckCircle2, XCircle, Plus, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

function StatusPill({ status, t }) {
  const cls = {
    published: "fl-status fl-status-published",
    pending: "fl-status fl-status-pending",
    rejected: "fl-status fl-status-rejected",
    paused: "fl-status fl-status-paused",
    draft: "fl-status fl-status-draft",
  }[status] || "fl-status fl-status-paused";
  return <span className={cls}>{t.status[status] || status}</span>;
}

export default function Admin() {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [props, setProps] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [rejectDialog, setRejectDialog] = useState({ open: false, prop: null, reason: "" });

  const loadCore = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data.items || []));
  };
  const loadProps = () => api.get(`/admin/properties${statusFilter ? `?status_filter=${statusFilter}` : ""}`).then((r) => setProps(r.data.items || []));
  useEffect(() => { loadCore(); }, []);
  useEffect(() => { loadProps(); }, [statusFilter]);

  const approve = async (p) => { await api.patch(`/admin/properties/${p.id}/approve`); toast.success("تمت الموافقة"); loadProps(); loadCore(); };
  const openReject = (p) => setRejectDialog({ open: true, prop: p, reason: "" });
  const doReject = async () => {
    if (!rejectDialog.reason.trim()) return toast.error("اكتب سبب الرفض");
    await api.patch(`/admin/properties/${rejectDialog.prop.id}/reject`, { reason: rejectDialog.reason });
    setRejectDialog({ open: false, prop: null, reason: "" });
    toast.success("تم الرفض"); loadProps(); loadCore();
  };
  const toggleFeature = async (p) => {
    await api.patch(`/admin/properties/${p.id}/feature`, { featured: !p.featured });
    toast.success("تم التحديث"); loadProps();
  };
  const removeProp = async (p) => {
    if (!window.confirm("حذف الإعلان؟")) return;
    await api.delete(`/properties/${p.id}`); loadProps(); loadCore();
  };
  const removeUser = async (u) => {
    if (u.role === "admin") return toast.error("لا يمكن حذف مدير");
    if (!window.confirm("حذف المستخدم وكل عقاراته؟")) return;
    await api.delete(`/admin/users/${u.id}`); loadCore(); loadProps();
  };

  const statLabels = lang === "ar"
    ? { total_users: "المستخدمون", total_properties: "العقارات", published: "منشورة", pending: "قيد المراجعة", paused: "متوقفة", rejected: "مرفوضة", total_views: "المشاهدات" }
    : { total_users: "Users", total_properties: "Properties", published: "Published", pending: "Pending", paused: "Paused", rejected: "Rejected", total_views: "Views" };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-emerald-950 mb-6">{t.admin.title}</h1>
      <Tabs defaultValue="stats">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="stats" data-testid="admin-tab-stats">{t.admin.stats}</TabsTrigger>
          <TabsTrigger value="pending" data-testid="admin-tab-pending">{t.admin.pending}</TabsTrigger>
          <TabsTrigger value="props" data-testid="admin-tab-props">{t.admin.properties}</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">{t.admin.users}</TabsTrigger>
          <TabsTrigger value="locations" data-testid="admin-tab-locations">{t.admin.locations}</TabsTrigger>
          <TabsTrigger value="cats" data-testid="admin-tab-cats">{t.admin.categories}</TabsTrigger>
          <TabsTrigger value="content" data-testid="admin-tab-content">محتوى الموقع</TabsTrigger>
        </TabsList>

        <TabsContent value="stats" className="mt-4">
          {stats ? (
            <div className="grid gap-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                {["total_users","total_properties","published","pending","paused","rejected","total_views"].map((k) => (
                  <div key={k} className="bg-white rounded-2xl border border-slate-200 p-4">
                    <div className="text-xs text-slate-500">{statLabels[k]}</div>
                    <div className="fl-num text-2xl font-extrabold text-emerald-950">{formatPrice(stats[k])}</div>
                  </div>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl border border-slate-200 p-4">
                  <div className="font-bold text-emerald-950 mb-2">{t.admin.byType}</div>
                  {Object.entries(stats.by_type || {}).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-1 border-b border-slate-100"><span>{typeLabel(k, lang)}</span><span className="font-bold">{v}</span></div>)}
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-4">
                  <div className="font-bold text-emerald-950 mb-2">{t.admin.byGov}</div>
                  {Object.entries(stats.by_governorate || {}).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-1 border-b border-slate-100"><span>{k}</span><span className="font-bold">{v}</span></div>)}
                </div>
              </div>
            </div>
          ) : <div className="text-slate-500">{t.common.loading}</div>}
        </TabsContent>

        <TabsContent value="pending" className="mt-4">
          <PropsList
            items={props.filter((p) => p.status === "pending")}
            t={t} lang={lang}
            onApprove={approve} onReject={openReject} onDelete={removeProp}
          />
        </TabsContent>

        <TabsContent value="props" className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm">{t.filters.filter}:</Label>
            <Select value={statusFilter || "any"} onValueChange={(v) => setStatusFilter(v === "any" ? "" : v)}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t.common.any}</SelectItem>
                {["published","pending","paused","rejected","draft"].map((s) => (
                  <SelectItem key={s} value={s}>{t.status[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <PropsList
            items={props} t={t} lang={lang}
            onApprove={approve} onReject={openReject} onDelete={removeProp}
            onFeature={toggleFeature}
          />
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="grid gap-2">
            {users.map((u) => (
              <div key={u.id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-900 text-amber-300 grid place-items-center font-bold">{(u.name || u.email)[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-emerald-950 truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email} · {u.phone || "-"}</div>
                </div>
                <Badge className={u.role === "admin" ? "bg-amber-500 text-emerald-950" : "bg-slate-200 text-slate-700 hover:bg-slate-200"}>{u.role}</Badge>
                <Button size="sm" variant="outline" onClick={() => removeUser(u)} className="text-rose-600"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="locations" className="mt-4">
          <LocationsManager />
        </TabsContent>

        <TabsContent value="cats" className="mt-4">
          <CategoriesManager />
        </TabsContent>

        <TabsContent value="content" className="mt-4">
          <SiteContentManager />
        </TabsContent>
      </Tabs>

      {/* Reject dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(v) => setRejectDialog({ ...rejectDialog, open: v })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t.admin.rejectReason}</DialogTitle></DialogHeader>
          <Textarea rows={4} value={rejectDialog.reason} onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
                    placeholder={lang === "ar" ? "اذكر سبب الرفض ليصل إلى المعلن" : "State why this listing is rejected"} data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, prop: null, reason: "" })}>{t.dash.cancel}</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={doReject} data-testid="reject-confirm">{t.admin.reject}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PropsList({ items, t, lang, onApprove, onReject, onDelete, onFeature }) {
  if (items.length === 0) return <div className="text-center py-12 text-slate-500 bg-white rounded-2xl border border-slate-200">{t.common.noResults}</div>;
  return (
    <div className="grid gap-2">
      {items.map((p) => (
        <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <span className="fl-status fl-status-paused">{typeLabel(p.type, lang)}</span>
          <StatusPill status={p.status} t={t} />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-emerald-950 truncate">{p.title}</div>
            <div className="text-xs text-slate-500 truncate">{p.governorate}{p.city ? ` · ${p.city}` : ""}{p.area ? ` · ${p.area}` : ""} · {p.owner_name}</div>
            {p.rejection_reason && <div className="text-xs text-rose-700 mt-0.5">✕ {p.rejection_reason}</div>}
          </div>
          <div className="fl-num text-sm font-bold text-emerald-950 hidden md:block">{formatPrice(p.price)}</div>
          <div className="flex items-center gap-1 flex-wrap">
            <a href={`/property/${p.id}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Eye className="w-4 h-4" /></Button></a>
            {p.status !== "published" && (
              <Button size="sm" onClick={() => onApprove(p)} data-testid={`approve-${p.id}`} className="bg-emerald-700 hover:bg-emerald-800 gap-1"><CheckCircle2 className="w-4 h-4" /> {t.admin.approve}</Button>
            )}
            {p.status !== "rejected" && (
              <Button size="sm" variant="outline" onClick={() => onReject(p)} data-testid={`reject-${p.id}`} className="text-rose-600 gap-1"><XCircle className="w-4 h-4" /> {t.admin.reject}</Button>
            )}
            {onFeature && (
              <Button size="sm" variant="outline" onClick={() => onFeature(p)}>{p.featured ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}</Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onDelete(p)} className="text-rose-600"><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function LocationsManager() {
  const { t } = useLang();
  const [govs, setGovs] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selGov, setSelGov] = useState(null);
  const [selCity, setSelCity] = useState(null);
  const [newG, setNewG] = useState(""); const [newC, setNewC] = useState(""); const [newA, setNewA] = useState("");

  const loadGovs = () => api.get("/locations/governorates").then((r) => setGovs(r.data.items || []));
  useEffect(() => { loadGovs(); }, []);
  useEffect(() => {
    if (!selGov) { setCities([]); return; }
    api.get(`/locations/cities?governorate=${encodeURIComponent(selGov.name)}`).then((r) => setCities(r.data.items || []));
  }, [selGov]);
  useEffect(() => {
    if (!selCity || !selGov) { setAreas([]); return; }
    api.get(`/locations/areas?governorate=${encodeURIComponent(selGov.name)}&city=${encodeURIComponent(selCity.name)}`).then((r) => setAreas(r.data.items || []));
  }, [selCity, selGov]);

  const addGov = async () => { if (!newG.trim()) return; await api.post("/admin/locations/governorates", { name: newG }); setNewG(""); loadGovs(); };
  const delGov = async (g) => { if (!window.confirm(`حذف ${g.name}؟ سيتم حذف مدنها ومناطقها`)) return; await api.delete(`/admin/locations/governorates/${g.id}`); setSelGov(null); loadGovs(); };
  const addCity = async () => { if (!newC.trim() || !selGov) return; await api.post("/admin/locations/cities", { name: newC, parent_id: selGov.id }); setNewC(""); const r = await api.get(`/locations/cities?governorate=${encodeURIComponent(selGov.name)}`); setCities(r.data.items || []); };
  const delCity = async (c) => { if (!window.confirm(`حذف ${c.name}؟`)) return; await api.delete(`/admin/locations/cities/${c.id}`); setSelCity(null); const r = await api.get(`/locations/cities?governorate=${encodeURIComponent(selGov.name)}`); setCities(r.data.items || []); };
  const addArea = async () => { if (!newA.trim() || !selCity) return; await api.post("/admin/locations/areas", { name: newA, parent_id: selCity.id }); setNewA(""); const r = await api.get(`/locations/areas?governorate=${encodeURIComponent(selGov.name)}&city=${encodeURIComponent(selCity.name)}`); setAreas(r.data.items || []); };
  const delArea = async (a) => { if (!window.confirm(`حذف ${a.name}؟`)) return; await api.delete(`/admin/locations/areas/${a.id}`); const r = await api.get(`/locations/areas?governorate=${encodeURIComponent(selGov.name)}&city=${encodeURIComponent(selCity.name)}`); setAreas(r.data.items || []); };

  const Col = ({ title, items, selId, onSel, onDel, newVal, setNew, onAdd, addTestId, disabled }) => (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="font-bold text-emerald-950 mb-3">{title}</div>
      <div className="flex gap-2 mb-3">
        <Input value={newVal} onChange={(e) => setNew(e.target.value)} placeholder={t.admin.addNew} disabled={disabled} />
        <Button data-testid={addTestId} onClick={onAdd} disabled={disabled} size="sm" className="bg-emerald-900 hover:bg-emerald-950"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="max-h-[420px] overflow-y-auto fl-scroll space-y-1">
        {items.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">—</div>}
        {items.map((x) => (
          <div key={x.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm ${selId === x.id ? "bg-emerald-50 text-emerald-900 font-semibold" : "hover:bg-slate-50"}`}
               onClick={() => onSel?.(x)}>
            <span className="flex-1">{x.name}</span>
            <button onClick={(e) => { e.stopPropagation(); onDel(x); }} className="text-rose-600 opacity-70 hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="grid md:grid-cols-3 gap-3">
      <Col title={t.admin.governorates} items={govs} selId={selGov?.id} onSel={setSelGov} onDel={delGov} newVal={newG} setNew={setNewG} onAdd={addGov} addTestId="add-gov" />
      <Col title={t.admin.cities} items={cities} selId={selCity?.id} onSel={setSelCity} onDel={delCity} newVal={newC} setNew={setNewC} onAdd={addCity} addTestId="add-city" disabled={!selGov} />
      <Col title={t.admin.areas} items={areas} onDel={delArea} newVal={newA} setNew={setNewA} onAdd={addArea} addTestId="add-area" disabled={!selCity} />
    </div>
  );
}

function CategoriesManager() {  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ key: "", name_ar: "", name_en: "", active: true });
  const load = () => api.get("/categories").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.key || !form.name_ar || !form.name_en) return toast.error("املأ الحقول");
    await api.post("/admin/categories", form);
    setForm({ key: "", name_ar: "", name_en: "", active: true });
    load();
  };
  const del = async (c) => { if (!window.confirm("حذف؟")) return; await api.delete(`/admin/categories/${c.id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 grid sm:grid-cols-4 gap-2">
        <Input placeholder="key (e.g. villa)" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} />
        <Input placeholder="اسم بالعربية" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
        <Input placeholder="Name (English)" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
        <Button onClick={add} className="bg-emerald-900 hover:bg-emerald-950"><Plus className="w-4 h-4 me-1" /> {t.admin.addNew}</Button>
      </div>
      <div className="grid gap-2">
        {items.map((c) => (
          <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3">
            <Badge className="bg-emerald-900">{c.key}</Badge>
            <div className="flex-1 font-semibold text-emerald-950">{c.name_ar} · {c.name_en}</div>
            <Button size="sm" variant="outline" onClick={() => del(c)} className="text-rose-600"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

const CONTENT_FIELDS = [
  { g: "الصفحة الرئيسية", items: [
    { k: "hero_title_ar", l: "العنوان (AR)" },
    { k: "hero_title_en", l: "Hero title (EN)" },
    { k: "hero_sub_ar", l: "الوصف (AR)", area: true },
    { k: "hero_sub_en", l: "Subtitle (EN)", area: true },
    { k: "launch_message_ar", l: "رسالة الإطلاق (AR)" },
    { k: "launch_message_en", l: "Launch message (EN)" },
    { k: "launch_active", l: "تفعيل عبارة الإطلاق", bool: true },
  ]},
  { g: "معلومات التواصل", items: [
    { k: "contact_phone", l: "الهاتف" },
    { k: "contact_whatsapp", l: "واتساب" },
    { k: "contact_email", l: "البريد الإلكتروني" },
    { k: "contact_address_ar", l: "العنوان (AR)" },
    { k: "contact_address_en", l: "Address (EN)" },
    { k: "instagram_url", l: "Instagram URL" },
    { k: "facebook_url", l: "Facebook URL" },
  ]},
  { g: "الفوتر و SEO", items: [
    { k: "footer_tagline_ar", l: "شعار الفوتر (AR)" },
    { k: "footer_tagline_en", l: "Footer tagline (EN)" },
    { k: "seo_title_ar", l: "SEO Title (AR)" },
    { k: "seo_title_en", l: "SEO Title (EN)" },
    { k: "seo_description_ar", l: "SEO Description (AR)", area: true },
    { k: "seo_description_en", l: "SEO Description (EN)", area: true },
    { k: "seo_keywords", l: "الكلمات المفتاحية", area: true },
  ]},
];

function SiteContentManager() {
  const { refresh: refreshSite } = useSiteContent();
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const load = async () => {
    const { data } = await api.get("/admin/site-content");
    setState(data); setDraft(data.draft || {});
  };
  useEffect(() => { load(); }, []);
  const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const saveDraft = async () => {
    setSaving(true);
    try { await api.put("/admin/site-content/draft", { data: draft }); toast.success("تم حفظ المسودة"); await load(); }
    catch { toast.error("فشل الحفظ"); }
    finally { setSaving(false); }
  };
  const publishNow = async () => {
    if (!window.confirm("نشر التعديلات إلى الموقع العام؟")) return;
    setSaving(true);
    try {
      await api.put("/admin/site-content/draft", { data: draft });
      await api.post("/admin/site-content/publish");
      toast.success("تم نشر التعديلات بنجاح — ستظهر على الموقع فوراً");
      await load(); await refreshSite();
    } catch { toast.error("فشل النشر"); }
    finally { setSaving(false); }
  };
  const doRestore = async (id) => {
    if (!window.confirm("استعادة هذه النسخة كمسودة؟")) return;
    await api.post(`/admin/site-content/restore/${id}`);
    toast.success("استُعيدت كمسودة — راجعها ثم اضغط نشر");
    await load();
  };
  if (!state) return <div className="text-slate-500">جار التحميل...</div>;
  const previewData = previewMode ? draft : (state.published || {});
  return (
    <div className="space-y-4">
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex flex-wrap gap-2 items-center">
        <div className="text-sm">
          <div className="font-bold text-emerald-900">آخر نشر</div>
          <div className="text-xs text-slate-600">
            {state.published_at ? new Date(state.published_at).toLocaleString() : "—"}
            {state.published_by ? ` · ${state.published_by}` : ""}
          </div>
        </div>
        <div className="ms-auto flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setPreviewMode(!previewMode)} data-testid="content-preview">
            {previewMode ? "إخفاء المعاينة" : "معاينة المسودة"}
          </Button>
          <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid="content-save-draft">حفظ كمسودة</Button>
          <Button className="bg-emerald-900 hover:bg-emerald-950" onClick={publishNow} disabled={saving} data-testid="content-publish">
            نشر التعديلات
          </Button>
        </div>
      </div>
      {previewMode && (
        <div className="bg-white rounded-2xl border-2 border-amber-300 p-5">
          <div className="text-xs font-bold text-amber-800 mb-2">معاينة</div>
          <div className="text-2xl font-extrabold text-emerald-950">{previewData.hero_title_ar}</div>
          <div className="text-sm text-slate-600 mt-1">{previewData.hero_sub_ar}</div>
          {previewData.launch_active !== false && (
            <div className="mt-2 inline-flex text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-3 py-1">{previewData.launch_message_ar}</div>
          )}
          <div className="mt-3 text-xs text-slate-500">هاتف {previewData.contact_phone} · واتساب {previewData.contact_whatsapp}</div>
        </div>
      )}
      {CONTENT_FIELDS.map((sec) => (
        <div key={sec.g} className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="font-bold text-emerald-950 mb-3">{sec.g}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {sec.items.map((f) => (
              <div key={f.k} className={f.area ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{f.l}</Label>
                {f.bool ? (
                  <div className="flex items-center gap-2 pt-2">
                    <Switch checked={draft[f.k] !== false} onCheckedChange={(v) => upd(f.k, v)} data-testid={`content-${f.k}`} />
                    <span className="text-xs text-slate-500">{draft[f.k] !== false ? "مفعّل" : "معطّل"}</span>
                  </div>
                ) : f.area ? (
                  <Textarea rows={2} value={draft[f.k] || ""} onChange={(e) => upd(f.k, e.target.value)} data-testid={`content-${f.k}`} />
                ) : (
                  <Input value={draft[f.k] || ""} onChange={(e) => upd(f.k, e.target.value)} data-testid={`content-${f.k}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {state.history && state.history.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="font-bold text-emerald-950 mb-3">سجل النشر ({state.history.length})</div>
          <div className="space-y-1">
            {state.history.map((v) => (
              <div key={v.id} className="flex items-center gap-3 text-sm py-2 border-b border-slate-100">
                <div className="flex-1">
                  <div>{new Date(v.published_at).toLocaleString()}</div>
                  <div className="text-xs text-slate-500">{v.published_by}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => doRestore(v.id)} data-testid={`restore-${v.id}`}>
                  استعادة كمسودة
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
